import React, { useState, useEffect } from 'react';
import { 
  FaFacebook, 
  FaTwitter, 
  FaInstagram, 
  FaLinkedin, 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope 
} from 'react-icons/fa';

const DEFAULT_DATA = {
  logoUrl: "https://jgalfalah.com/backend/media/uploaded_images/uploaded_images/warm-wellness-center-logo-cs-2025-cs-removebg-preview.png",
  aboutText: "Warm Wellness Center's Commitment to Advanced Health Care Solutions.",
  quickLinks: [
    { text: "About Us", url: "#" },
    { text: "Services", url: "#" },
    { text: "Our Doctors", url: "#" },
    { text: "Health Packages", url: "#" },
    { text: "Career", url: "#" },
    { text: "Test Prices", url: "#" },
    { text: "Online Report", url: "#" }
  ],
  serviceLinks: [
    { text: "Doctor Consultation", url: "#" },
    { text: "Appointment Booking", url: "#" },
    { text: "Pharmacy", url: "#" },
    { text: "Media Center", url: "#" },
    { text: "Terms and Conditions", url: "#" },
    { text: "Privacy Policy", url: "#" },
    { text: "Contact Us", url: "#" }
  ],
  socialLinks: [
    { platform: "facebook", url: "#" },
    { platform: "twitter", url: "#" },
    { platform: "instagram", url: "#" },
    { platform: "linkedin", url: "#" }
  ],
  contactInfo: {
    address: "167, Tropical Cantt View, 1st floor, 4 Matikata Rd, Dhaka 1206",
    phone: "+880 9606 990123",
    email: "info@jghealthcare.com"
  },
  copyrightText: `© ${new Date().getFullYear()} Blockchain Technologies. All Rights Reserved.`,
  colors: {
    primary: '#0760ad',
    secondary: '#098a48',
    lightBg: 'rgba(255, 255, 255, 0.1)'
  }
};

const Footer = () => {
  const [footerData, setFooterData] = useState(DEFAULT_DATA);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/layout/footer/`);
        if (response.ok) {
          const data = await response.json();
          
          // Transform legacy data format (array of strings) to new format (array of objects)
          const transformLinks = (links) => {
            if (!links) return [];
            return links.map(link => 
              typeof link === 'string' ? { text: link, url: '#' } : link
            );
          };
          
          const transformedData = {
            ...DEFAULT_DATA,
            ...data,
            quickLinks: transformLinks(data.quickLinks),
            serviceLinks: transformLinks(data.serviceLinks)
          };
          
          setFooterData(transformedData);
        }
      } catch (error) {
        console.error('Error fetching footer data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFooterData();
  }, []);

  if (isLoading) {
    return (
      <footer className="bg-gradient-to-r from-blue-600 to-green-500 py-8">
        <div className="container mx-auto px-4 text-center text-white">
          <span className="loading loading-spinner loading-lg"></span>
          <p>Loading footer...</p>
        </div>
      </footer>
    );
  }

  const SocialIcon = ({ platform }) => {
    switch (platform) {
      case 'facebook': return <FaFacebook />;
      case 'twitter': return <FaTwitter />;
      case 'instagram': return <FaInstagram />;
      case 'linkedin': return <FaLinkedin />;
      default: return null;
    }
  };

  return (
    <footer 
      className="pt-16 pb-8"
      style={{
        background: `linear-gradient(135deg, ${footerData.colors.primary} 0%, ${footerData.colors.secondary} 100%)`,
        color: 'white'
      }}
    >
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-4 gap-12 mb-12">
          {/* Logo and About */}
          <div className="md:col-span-2 lg:col-span-1">
            <div className="flex items-center mb-6 bg-white mx-auto rounded-lg">
              <img 
                src={footerData.logoUrl} 
                alt="Warm Wellness Logo"
                className="h-20 w-auto mx-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = DEFAULT_DATA.logoUrl;
                }}
              />
            </div>
            <p className="mb-6 opacity-90">
              {footerData.aboutText}
            </p>
            
            {/* Social Media Links */}
            <div className="flex space-x-4 mb-6">
              {footerData.socialLinks.map((social, index) => (
                <a 
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 rounded-full transition-all hover:bg-white hover:bg-opacity-20"
                  style={{ fontSize: '1.2rem' }}
                >
                  <SocialIcon platform={social.platform} />
                </a>
              ))}
            </div>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 pb-2 border-b" style={{ borderColor: footerData.colors.lightBg }}>
              Quick Links
            </h4>
            <ul className="space-y-3">
              {footerData.quickLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.url}
                    className="hover:underline opacity-90 hover:opacity-100 transition-opacity flex items-center"
                  >
                    <span className="w-2 h-2 rounded-full bg-white mr-3 opacity-70"></span>
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6 pb-2 border-b" style={{ borderColor: footerData.colors.lightBg }}>
              Services
            </h4>
            <ul className="space-y-3">
              {footerData.serviceLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.url}
                    className="hover:underline opacity-90 hover:opacity-100 transition-opacity flex items-center"
                  >
                    <span className="w-2 h-2 rounded-full bg-white mr-3 opacity-70"></span>
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Contact Information */}
          <div>
            <h4 className="text-lg font-semibold mb-6 pb-2 border-b" style={{ borderColor: footerData.colors.lightBg }}>
              Contact Us
            </h4>
            <div className="space-y-4">
              <div className="flex items-start">
                <FaMapMarkerAlt className="mt-1 mr-3 flex-shrink-0" />
                <p className="opacity-90">
                  {footerData.contactInfo.address}
                </p>
              </div>
              
              <div className="flex items-center">
                <FaPhone className="mr-3 flex-shrink-0" />
                <a href={`tel:${footerData.contactInfo.phone}`} className="hover:underline opacity-90 hover:opacity-100">
                  {footerData.contactInfo.phone}
                </a>
              </div>
              
              <div className="flex items-center">
                <FaEnvelope className="mr-3 flex-shrink-0" />
                <a href={`mailto:${footerData.contactInfo.email}`} className="hover:underline opacity-90 hover:opacity-100">
                  {footerData.contactInfo.email}
                </a>
              </div>
              
              <a
                href={`https://maps.google.com?q=${encodeURIComponent(footerData.contactInfo.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 rounded-full font-semibold mt-4 transition-all hover:shadow-lg flex items-center"
                style={{ 
                  backgroundColor: 'white', 
                  color: footerData.colors.primary 
                }}
              >
                <FaMapMarkerAlt className="mr-2" />
                Get Directions
              </a>
            </div>
          </div>
        </div>
        
        {/* Copyright */}
        <div 
          className="border-t pt-8 text-center opacity-80 text-sm"
          style={{ borderColor: footerData.colors.lightBg }}
        >
          <p>{footerData.copyrightText}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;